(function () {
  const preorderFlag = '_mine_preorder';

  function route(path) {
    return `${window.Shopify.routes.root}${path}`;
  }

  function integer(value, fallback = 0) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function isPreorder(item) {
    return String(item.properties?.[preorderFlag] || '') === 'true';
  }

  function formProperties(formData) {
    const properties = {};

    for (const [name, value] of formData.entries()) {
      const match = name.match(/^properties\[(.+)\]$/);

      if (match && String(value).length > 0) {
        properties[match[1]] = value;
      }
    }

    return properties;
  }

  function preorderProperties(properties, key, value) {
    return {
      ...properties,
      [preorderFlag]: 'true',
      [key]: value
    };
  }

  function setLoading(form, loading) {
    form.querySelectorAll('[type="submit"]').forEach((button) => {
      if (loading) {
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
    });

    document.documentElement.dispatchEvent(new CustomEvent(loading ? 'theme:loading:start' : 'theme:loading:end', { bubbles: true }));
  }

  async function cart() {
    return fetch(route('cart.js')).then((response) => response.json());
  }

  function bundledSections() {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections }
    }));
    return sections;
  }

  async function addItems(items, variantId) {
    const sections = bundledSections();
    const response = await fetch(route('cart/add.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items,
        sections: sections.join(','),
        sections_url: `${window.Shopify.routes.root}variants/${variantId}`
      })
    });
    const responseJson = await response.json();

    if (!response.ok) {
      throw responseJson;
    }

    return responseJson;
  }

  async function announceAdd(form, responseJson) {
    const cartContent = await cart();
    cartContent.sections = responseJson.sections;

    form.dispatchEvent(new CustomEvent('variant:add', {
      bubbles: true,
      detail: {
        items: responseJson.items || responseJson,
        cart: cartContent
      }
    }));

    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent: 'variant:add',
        cart: cartContent
      }
    }));
  }

  async function splitProductAdd(event, form, metadata) {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (form.dataset.minePreorderBusy === 'true') {
      return;
    }

    form.dataset.minePreorderBusy = 'true';
    setLoading(form, true);

    try {
      const formData = new FormData(form);
      const variantId = integer(formData.get('id'));
      const requested = Math.max(1, integer(formData.get('quantity'), 1));
      const inventory = Math.max(0, integer(metadata.dataset.mineInventoryQuantity));
      const cartContent = await cart();
      const normalInCart = cartContent.items
        .filter((item) => item.variant_id === variantId && !isPreorder(item))
        .reduce((total, item) => total + item.quantity, 0);
      const normalQuantity = Math.min(requested, Math.max(0, inventory - normalInCart));
      const preorderQuantity = requested - normalQuantity;
      const properties = formProperties(formData);
      const sellingPlan = formData.get('selling_plan');
      const items = [];

      if (normalQuantity > 0) {
        const item = { id: variantId, quantity: normalQuantity, properties };
        if (sellingPlan) item.selling_plan = integer(sellingPlan);
        items.push(item);
      }

      if (preorderQuantity > 0) {
        const item = {
          id: variantId,
          quantity: preorderQuantity,
          properties: preorderProperties(
            properties,
            metadata.dataset.minePreorderKey,
            metadata.dataset.minePreorderValue
          )
        };
        if (sellingPlan) item.selling_plan = integer(sellingPlan);
        items.push(item);
      }

      const responseJson = await addItems(items, variantId);
      await announceAdd(form, responseJson);
    } catch (error) {
      form.dispatchEvent(new CustomEvent('cart:error', {
        bubbles: true,
        detail: { error: error.description || error.message || String(error) }
      }));
    } finally {
      setLoading(form, false);
      delete form.dataset.minePreorderBusy;
    }
  }

  function productSubmit(event) {
    const form = event.target;

    if (!(form instanceof HTMLFormElement) || !form.action.includes('/cart/add')) {
      return;
    }

    const metadata = form.querySelector('buy-buttons[data-mine-inventory-policy="continue"]');

    if (!metadata || integer(metadata.dataset.mineInventoryQuantity) <= 0) {
      return;
    }

    splitProductAdd(event, form, metadata);
  }

  function normalCapacity(lineItem) {
    const variantId = lineItem.dataset.mineVariantId;
    const inventory = Math.max(0, integer(lineItem.dataset.mineInventoryQuantity));
    const currentLineKey = lineItem.dataset.mineLineKey;
    const quantitiesByLine = new Map();

    Array.from(document.querySelectorAll(`line-item[data-mine-variant-id="${CSS.escape(variantId)}"][data-mine-is-preorder="false"]`)).forEach((item) => {
      const lineKey = item.dataset.mineLineKey;

      if (lineKey && lineKey !== currentLineKey) {
        quantitiesByLine.set(lineKey, integer(item.querySelector('[data-line-key]')?.value));
      }
    });

    const usedByOtherLines = Array.from(quantitiesByLine.values()).reduce((total, quantity) => total + quantity, 0);

    return Math.max(0, inventory - usedByOtherLines);
  }

  function cartTarget(event) {
    const control = event.target.closest('[data-line-key], a[href*="/cart/change"]');
    const controlLineKey = control?.getAttribute('data-line-key') || (control?.href ? new URL(control.href).searchParams.get('id') : '');
    const lineItem = control?.closest('line-item[data-mine-inventory-policy="continue"][data-mine-is-preorder="false"]')
      || document.querySelector(`line-item[data-mine-line-key="${CSS.escape(controlLineKey)}"][data-mine-inventory-policy="continue"][data-mine-is-preorder="false"]`);

    if (!control || !lineItem) {
      return null;
    }

    let quantity;

    if (control.matches('[data-line-key]')) {
      quantity = integer(control.value);
    } else {
      quantity = integer(new URL(control.href).searchParams.get('quantity'));
    }

    if (quantity <= normalCapacity(lineItem)) {
      return null;
    }

    return { control, lineItem, quantity };
  }

  async function splitCartChange(event, target) {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (target.lineItem.dataset.minePreorderBusy === 'true') {
      return;
    }

    target.lineItem.dataset.minePreorderBusy = 'true';
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const cartContent = await cart();
      const lineKey = target.lineItem.dataset.mineLineKey;
      const sourceLine = cartContent.items.find((item) => item.key === lineKey);
      const allowedNormal = normalCapacity(target.lineItem);
      const preorderQuantity = target.quantity - allowedNormal;

      if (!sourceLine || preorderQuantity <= 0) {
        return;
      }

      if (sourceLine.quantity !== allowedNormal) {
        const changeResponse = await fetch(route('cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: allowedNormal })
        });

        if (!changeResponse.ok) {
          throw await changeResponse.json();
        }
      }

      await addItems([{
        id: sourceLine.variant_id,
        quantity: preorderQuantity,
        properties: preorderProperties(
          sourceLine.properties || {},
          target.lineItem.dataset.minePreorderKey,
          target.lineItem.dataset.minePreorderValue
        ),
        ...(sourceLine.selling_plan_allocation?.selling_plan?.id
          ? { selling_plan: sourceLine.selling_plan_allocation.selling_plan.id }
          : {})
      }], sourceLine.variant_id);

      if (window.themeVariables.settings.pageType === 'cart') {
        window.location.reload();
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (error) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      delete target.lineItem.dataset.minePreorderBusy;
    }
  }

  document.addEventListener('submit', productSubmit, true);
  document.addEventListener('click', (event) => {
    const target = cartTarget(event);
    if (target) splitCartChange(event, target);
  }, true);
  document.addEventListener('change', (event) => {
    const target = cartTarget(event);
    if (target) splitCartChange(event, target);
  }, true);
})();
